import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '@/utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2, RefreshCw, ExternalLink } from 'lucide-react';
import { useAuth } from '@/App';
import { RnrDecisionDialog } from './RnrDecisionDialog';
import { ExceptionDecisionDialog } from './ExceptionDecisionDialog';

const titleize = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const fmt = (v) => { try { return new Date(v).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch { return v; } };

const KIND_CLS = {
  unassigned_on_enrol: 'bg-amber-100 text-amber-800',
  missing_counselling_outcome: 'bg-rose-100 text-rose-700',
  session_discrepancy: 'bg-purple-100 text-purple-800',
};

export default function ExceptionQueue() {
  const { user } = useAuth();
  const canDecide = ['admin', 'operating_head'].includes(user?.role);
  const [review, setReview] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get(`/workflow/exceptions?status=open`);
      setItems(r.data.exceptions || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load exceptions');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="exception-queue-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold text-[#1B2559] flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" /> Review</h1>
          <p className="text-sm text-slate-500">Workflow leads that need a manager's attention (unroutable, stuck or discrepancies)</p>
        </div>
        <Button size="sm" variant="outline" onClick={load} data-testid="eq-refresh"><RefreshCw className="w-4 h-4" /></Button>
      </div>

      {loading ? (
        <div className="p-10 text-center text-slate-400">Loading…</div>
      ) : items.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-slate-400" data-testid="eq-empty">
          <CheckCircle2 className="w-8 h-8 mx-auto text-emerald-400" />
          <p className="mt-2 text-sm">No open exceptions.</p>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {items.map((e) => (
            <Card key={e.id} data-testid={`eq-item-${e.id}`}>
              <CardHeader className="pb-2 pt-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Badge className={`text-[10px] border-0 ${KIND_CLS[e.kind] || 'bg-slate-100 text-slate-600'}`}>{titleize(e.kind)}</Badge>
                    <Link to={`/students/${e.lead_id}`} className="text-indigo-600 hover:underline flex items-center gap-1" data-testid={`eq-lead-${e.id}`}>
                      {e.lead_name || e.lead_id} <ExternalLink className="w-3 h-3" />
                    </Link>
                  </CardTitle>
                  <span className="text-[10px] text-slate-400">{fmt(e.created_at)}</span>
                </div>
              </CardHeader>
              <CardContent className="pt-0 pb-4">
                <p className="text-sm text-slate-700">{e.detail}</p>
                {e.next_action && <p className="text-xs text-slate-500 mt-1"><span className="font-medium">Next:</span> {e.next_action}</p>}
                <div className="mt-3 flex items-center justify-between">
                  <Badge data-testid={`eq-status-${e.id}`} className={`text-[10px] border-0 ${e.status === 'open' ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'}`}>{e.pending_decision_id ? 'Save incomplete' : titleize(e.status)}</Badge>
                  {e.kind === 'rnr_review' && canDecide && (!e.decision_id || e.pending_decision_id) && <Button asChild size="sm" className="min-h-[44px]" data-testid={`review-rnr-${e.id}`}><Link to={`/students/${e.lead_id}`}>{e.pending_decision_id ? 'Resume saved decision' : 'Review & decide'}</Link></Button>}
                  {!canDecide && e.status === 'open' && <p className="text-xs text-amber-800" data-testid={`exception-manager-only-${e.id}`}>Admin / OH decision required</p>}
                  {e.kind !== 'rnr_review' && canDecide && <Button size="sm" className="min-h-[44px]" onClick={() => setReview(e)} data-testid={`review-exception-${e.id}`}>{e.pending_decision_id ? 'Resume saved decision' : 'Review & decide'}</Button>}
                </div>
                {e.resolution_summary && <p className="text-sm text-teal-800 mt-2 break-words" data-testid={`exception-resolution-${e.id}`}>{e.resolution_summary}</p>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {review && (review.kind === 'rnr_review' ? <RnrDecisionDialog exception={review} onClose={() => setReview(null)} onSaved={() => { setReview(null); load(); }} /> : <ExceptionDecisionDialog exception={review} onClose={() => setReview(null)} onSaved={() => { setReview(null); load(); }} />)}
    </div>
  );
}
